/**
 * A robust helper function to convert an array of objects or an object of objects
 * into a single flat object keyed by the 'id' property. This is crucial for
 * creating efficient, hashmap-style lookups throughout the application.
 * @param {Array|Object} data - The data to process. Can be an array of objects or an object containing objects.
 * @returns {Object} A flattened object keyed by item IDs.
 */
const keyById = (data) => {
  if (Array.isArray(data)) {
    // Standard case: an array of items, each with an 'id'.
    return data.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }
  // Advanced case: an object containing sub-objects (like weapons.json).
  // This flattens all nested items into a single top-level object.
  return Object.values(data).reduce((acc, category) => {
    // The `category` here is an array of items (e.g., the array for 'primary_physical').
    category.forEach(item => {
      acc[item.id] = item;
    });
    return acc;
  }, {});
};


/**
 * Fetches all core Daggerheart game data from the public JSON files.
 * This function is designed to be called once on application startup.
 * It also transforms the raw data into a more optimal structure for component consumption.
 * @returns {Promise<object>} A promise that resolves to an object containing all game data.
 */
/**
 * Load homebrew campaign frames from Firestore.
 * Returns an object keyed by frameId, matching the shape of campaign_frames.json entries.
 * Private by default — only loads the current user's homebrew.
 *
 * TODO (Issue #82): Query Firestore 'users/{userId}/homebrewFrames' collection
 * @returns {Promise<Object>} Homebrew frames keyed by frameId, or empty object
 */
export const loadHomebrewFrames = async () => {
  // Stub — returns empty object until homebrew system is built
  return {};
};

/**
 * Load composed mechanics from Firestore for a user.
 * Returns an array of mechanic documents with { id, type, status, meta, data }.
 * Only loads 'ready' mechanics (drafts stay in Workshop only).
 *
 * Full CRUD is Issue #179. This provides read-only loading for dual-path routing.
 *
 * @param {string} userId - Firebase auth UID
 * @returns {Promise<Array>} Array of composed mechanic documents, or empty array
 */
export const loadComposedMechanics = async (userId) => {
  if (!userId) return [];
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    const snapshot = await getDocs(
      query(
        collection(db, 'users', userId, 'homebrewContent'),
        where('type', '==', 'mechanic'),
        where('status', '==', 'ready')
      )
    );
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Failed to load composed mechanics:', error);
    return [];
  }
};

export const loadGameData = async () => {
  try {
    const responses = await Promise.all([
      fetch('/data/core_data/classes.json'),
      fetch('/data/core_data/subclasses.json'),
      fetch('/data/core_data/ancestries.json'),
      fetch('/data/core_data/communities.json'),
      fetch('/data/core_data/weapons.json'),
      fetch('/data/core_data/armor.json'),
      fetch('/data/domain_cards.json'),
      fetch('/data/campaign_frames.json'),
      fetch('/data/loot.json'),
      fetch('/data/equipment.json'),
      fetch('/data/class_guidance.json')
    ]);

    // Debug: log fetch status
    responses.forEach((res, i) => {
      if (!res.ok) {
        console.error(`Fetch failed for index ${i}:`, res.url, res.status, res.statusText);
      }
    });

    const [
      classes, subclasses, ancestries, communities, weapons, armor,
      domainCards, campaignFrames, loot, equipment, classGuidance
    ] = await Promise.all(responses.map(async (res, i) => {
      try {
        return await res.json();
      } catch (e) {
        console.error(`JSON parse error for file index ${i}:`, res.url, e);
        debugger;
        throw e;
      }
    }));

    // The raw weapon data is nested (e.g., weapons.primary_physical).
    // The raw armor data is a simple array.
    // The raw campaign data contains nested equipment arrays.
    // We process them into flat objects keyed by ID for efficient lookup.
    const processedCampaignFrames = Object.fromEntries(
        Object.entries(campaignFrames).map(([key, frame]) => [key, frame])
    );

    // Merge homebrew frames (private per-user, loaded from Firestore)
    // TODO: Implement loadHomebrewFrames() when homebrew system is built (Issue #82)
    const homebrewFrames = await loadHomebrewFrames();
    Object.assign(processedCampaignFrames, homebrewFrames);

    // Process comprehensive equipment data into flat structures
  const processEquipmentData = (equipmentData, type) => {
    const flattened = {};
    
    Object.entries(equipmentData).forEach(([tierKey, tierData]) => {
      // Check if this tier has physical/magic subdivisions (primary weapons)
      // or if items are directly under the tier (secondary weapons, armor)
      const hasSubdivisions = tierData.physical || tierData.magic;
      
      if (hasSubdivisions) {
        // Handle primary weapons with physical/magic subdivisions
        Object.entries(tierData).forEach(([category, categoryData]) => {
          if (typeof categoryData === 'object' && categoryData !== null) {
            Object.entries(categoryData).forEach(([itemKey, itemData]) => {
              if (itemData && typeof itemData === 'object') {
                const processedItem = {
                  ...itemData,
                  type: type,
                  tier: parseInt(tierKey.replace('tier', '')),
                  category: category
                };
                flattened[itemKey] = processedItem;
              }
            });
          }
        });
      } else {
        // Handle secondary weapons and armor (items directly under tier)
        Object.entries(tierData).forEach(([itemKey, itemData]) => {
          if (itemData && typeof itemData === 'object') {
            const processedItem = {
              ...itemData,
              type: type,
              tier: parseInt(tierKey.replace('tier', ''))
            };
            flattened[itemKey] = processedItem;
          }
        });
      }
    });
    
    return flattened;
  };

    const result = {
      classes: keyById(classes.classes),
      subclasses: keyById(subclasses.subclasses),
      ancestries: keyById(ancestries.ancestries),
      communities: keyById(communities.communities),
      weapons: keyById(weapons.weapons),
      armor: keyById(armor.armor),
      domainCards: keyById(domainCards.domain_cards),
      campaignFrames: processedCampaignFrames,
      loot: loot,
      // Comprehensive equipment system
      equipmentDatabase: {
        primaryWeapons: processEquipmentData(equipment.primaryWeapons || {}, 'primaryWeapon'),
        secondaryWeapons: processEquipmentData(equipment.secondaryWeapons || {}, 'secondaryWeapon'),
        armor: processEquipmentData(equipment.armor || {}, 'armor')
      },
      // Class-specific guidance for character creation
      classGuidance: classGuidance
    };

    return result;

  } catch (error) {
    console.error("Failed to load game data (outer catch):", error);
    debugger;
    return null;
  }
};
